import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import useSWR from 'swr';
import { listProducts } from '../lib/api';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { ShoppingBag, Heart, Shield, Truck, RotateCcw, Award, Minus, Plus, Star, ChevronRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useToast } from '../hooks/use-toast';
import { Skeleton } from '../components/ui/skeleton';

import SmartImage from '../components/SmartImage';

const ProductDetail = () => {
  const { id } = useParams();
  const { data: allProducts, error, isLoading } = useSWR('publicProducts', listProducts);
  
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const { addToCart } = useCart();
  const { toast } = useToast();

  const productsToShow = useMemo(() => {
    return allProducts || [];
  }, [allProducts]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-2 gap-10">
        <Skeleton className="w-full aspect-square" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-8 w-1/4" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const product = productsToShow.find(p => p.id.toString() === id.toString());

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="text-lg text-neutral-600">Product not found.</p>
        <Link to="/shop" className="text-[#b8892d] hover:underline mt-4 inline-block font-medium">Back to Shop</Link>
      </div>
    );
  }

  const related = productsToShow.filter(p => p.id !== product.id && (p.brand === product.brand || p.gender === product.gender)).slice(0, 4);

  const images = product.images && product.images.length > 0 ? product.images : [product.image];

  return (
    <div className="bg-white">
      <div className="bg-neutral-100 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-2 text-xs text-neutral-500">
          <Link to="/" className="hover:text-[#b8892d]">Home</Link>
          <ChevronRight size={12}/>
          <Link to="/shop" className="hover:text-[#b8892d]">Shop</Link>
          <ChevronRight size={12}/>
          <span className="text-neutral-800 truncate">{product.title}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
        <div className="space-y-4">
          <div className="relative bg-neutral-50 h-fit overflow-hidden">
            <SmartImage 
              src={images[activeImg]} 
              alt={product.title} 
              containerClassName="w-full h-auto aspect-square"
              className="object-contain max-h-[600px] mx-auto shadow-sm"
            />
            {product.discount > 0 && (
              <span className="absolute top-4 left-4 bg-[#b8892d] text-white text-xs px-3 py-1.5 tracking-wider z-10">-{product.discount}% SALE</span>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {images.map((img, i) => (
                <button 
                  key={i} 
                  onClick={() => setActiveImg(i)}
                  className={`border-2 bg-neutral-50 aspect-square overflow-hidden transition-all ${activeImg === i ? 'border-[#b8892d]' : 'border-transparent hover:border-neutral-200'}`}
                >
                  <SmartImage src={img} alt={`${product.title} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-[#b8892d] text-xs tracking-[0.3em] uppercase font-medium mb-2">{product.brand} · {product.category}</div>
          <h1 className="font-serif text-2xl md:text-3xl text-[#1a1a1a] leading-tight mb-4">{product.title}</h1>
          <div className="flex items-center gap-2 mb-5">
            <div className="flex gap-0.5">{Array.from({length:5}).map((_,i) => <Star key={`pd-star-${i}`} size={14} className="fill-[#b8892d] text-[#b8892d]"/>)}</div>
            <span className="text-xs text-neutral-500">(24 reviews)</span>
          </div>
          <div className="flex items-baseline gap-3 mb-6 pb-6 border-b border-neutral-200">
            <span className="text-neutral-400 line-through">₹{product.price.toLocaleString('en-IN')}</span>
            <span className="text-3xl font-serif text-[#b8892d]">₹{product.sale.toLocaleString('en-IN')}</span>
            <span className="text-xs bg-red-50 text-red-700 px-2 py-1 font-medium">Save ₹{(product.price - product.sale).toLocaleString('en-IN')}</span>
          </div>
          <div className="space-y-3 text-sm text-neutral-700 mb-6">
            {product.description && (
              <p className="whitespace-pre-line leading-relaxed">{product.description}</p>
            )}
            {product.specs && (
              <ul className="space-y-1.5">
                <li className="flex gap-2 font-medium text-xs uppercase tracking-wider text-neutral-500 mt-4 mb-2">Key Specifications</li>
                {product.specs.split('\n').filter(s => s.trim()).map((spec, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#b8892d]">•</span> {spec}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center border border-neutral-300">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-11 flex items-center justify-center hover:bg-neutral-100"><Minus size={14}/></button>
              <span className="w-12 text-center">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-11 flex items-center justify-center hover:bg-neutral-100"><Plus size={14}/></button>
            </div>
            <Button
              onClick={() => { addToCart(product, qty); toast({ title: 'Added to Basket', description: product.title.slice(0, 40) + '...' }); }}
              className="flex-1 bg-[#b8892d] hover:bg-[#a07826] h-11 uppercase tracking-widest text-xs gap-2"
            >
              <ShoppingBag size={16}/> Add to Basket
            </Button>
            <button className="w-11 h-11 border border-neutral-300 flex items-center justify-center hover:border-[#b8892d] hover:text-[#b8892d]"><Heart size={18}/></button>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-6 border-t border-neutral-200">
            {[{i:Shield,t:'1-2 Year Warranty'},{i:Truck,t:'Free Delivery'},{i:RotateCcw,t:'Easy Returns'},{i:Award,t:'100% Swiss ETA'}].map((f)=>(
              <div key={f.t} className="flex items-center gap-2 text-sm text-neutral-700">
                <f.i size={16} className="text-[#b8892d]"/> {f.t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-neutral-50 py-14">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="font-serif text-2xl md:text-3xl text-[#1a1a1a] mb-6">You May Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.map(p => <ProductCard key={p.id} product={p}/>)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
